import { RequestStatusBadge } from "@/components/common/section-components/badge/RequestStatusBadge";
import { EstablishmentFieldRequestStatus } from "@/constants/entity-status";
import { RequestTypes, TipoOperacionSolicitudEstablecimiento } from "@/constants/enums";
import { EstablishmentFieldRequestDetail } from "@/services/establishment-field-request.service";
import { AppColors } from "@/styles/colors";
import { Box, Flex, Text } from "@chakra-ui/react";
import dayjs from "dayjs";
import { Pencil } from "lucide-react";

const TIPO_CAMPO_LABELS: Record<number, string> = { 1: "Propio", 2: "Alquilado" };

const InfoRow = ({ label, value, even }: { label: string; value: string; even: boolean }) => (
  <Flex
    padding="10px 12px"
    bgColor={even ? AppColors.BACKGROUND_SECONDARY : "transparent"}
    borderRadius="4px"
    gap="4px"
  >
    <Text fontSize="14px" color={AppColors.GRAY_TEXT} fontWeight={500}>
      {label}:
    </Text>
    <Text fontSize="14px" color={AppColors.GRAY_TEXT}>
      {value}
    </Text>
  </Flex>
);

const EstadoRow = ({ estado, even }: { estado: EstablishmentFieldRequestStatus; even: boolean }) => (
  <Flex
    padding="10px 12px"
    bgColor={even ? AppColors.BACKGROUND_SECONDARY : "transparent"}
    borderRadius="4px"
    gap="4px"
    alignItems="center"
  >
    <Text fontSize="14px" color={AppColors.GRAY_TEXT} fontWeight={500}>
      Estado:
    </Text>
    <RequestStatusBadge status={estado} noPaddingY />
  </Flex>
);

interface Props {
  detail: EstablishmentFieldRequestDetail;
  pendingTipoEstablecimiento: number | null;
  isPending: boolean;
  canWrite: boolean;
  onOpenTipoCampoModal: () => void;
}

export const EstablishmentFieldRequestDetailCards = ({
  detail,
  pendingTipoEstablecimiento,
  isPending,
  canWrite,
  onOpenTipoCampoModal,
}: Props) => {
  const isLote = detail.idTipoSolicitud === RequestTypes.SolicitudLote;
  const isAltaLote = detail.tipoOperacionId === TipoOperacionSolicitudEstablecimiento.Alta;
  const isAltaEstablecimiento =
    !isLote && detail.tipoOperacionId === TipoOperacionSolicitudEstablecimiento.Alta;
  const hasCoordinates = Boolean(detail.latitud || detail.longitud);
  const coordenadasStr = `${detail.latitud?.toFixed(4)}, ${detail.longitud?.toFixed(4)}`;

  return (
    <Flex flexDir="column" gap="6px">
      <Text fontSize="11px" fontWeight={700} color={AppColors.GRAY_TEXT_ALT} letterSpacing="1px">
        DETALLE
      </Text>
      <Flex gap="16px" flexWrap="wrap">
        {/* Left card */}
        <Box
          flex="1"
          minWidth="260px"
          border={`1px solid ${AppColors.WHITE_BORDER_1}`}
          borderRadius="8px"
          padding="16px"
        >
          <Flex flexDir="column">
            <InfoRow label="Tipo" value={detail.tipoOperacion} even />
            <InfoRow
              label="Fecha"
              value={detail.fecha ? dayjs(detail.fecha).format("DD/MM/YYYY") : "-"}
              even={false}
            />
            <InfoRow label="Titular Cuenta" value={detail.titularCuenta} even />
            {isLote ? (
              <InfoRow label="Nombre lote" value={detail.nombreLote || "-"} even={false} />
            ) : (
              <InfoRow
                label="Nombre establecimiento"
                value={detail.establecimiento || "-"}
                even={false}
              />
            )}
          </Flex>
        </Box>

        {/* Right card */}
        <Box
          flex="1"
          minWidth="260px"
          border={`1px solid ${AppColors.WHITE_BORDER_1}`}
          borderRadius="8px"
          padding="16px"
        >
          <Flex flexDir="column">
            {isLote ? (
              <>
                <InfoRow label="Establecimiento" value={detail.establecimiento || "-"} even />
                {detail.hectareas != null && (
                  <InfoRow
                    label="Hectáreas"
                    value={detail.hectareas.toLocaleString("es-AR")}
                    even={false}
                  />
                )}
                {!isAltaLote && hasCoordinates && (
                  <InfoRow
                    label="Coordenadas"
                    value={coordenadasStr}
                    even={detail.hectareas != null}
                  />
                )}
                <EstadoRow
                  estado={detail.estado as EstablishmentFieldRequestStatus}
                  even={
                    (1 +
                      (detail.hectareas != null ? 1 : 0) +
                      (!isAltaLote && hasCoordinates ? 1 : 0)) %
                      2 ===
                    0
                  }
                />
              </>
            ) : (
              <>
                <Flex
                  padding="10px 12px"
                  bgColor={AppColors.BACKGROUND_SECONDARY}
                  borderRadius="4px"
                  gap="4px"
                  alignItems="center"
                >
                  <Text fontSize="14px" color={AppColors.GRAY_TEXT} fontWeight={500}>
                    Tipo de establecimiento:
                  </Text>
                  <Text
                    fontSize="14px"
                    color={AppColors.GRAY_TEXT}
                    fontStyle={pendingTipoEstablecimiento != null ? "italic" : "normal"}
                    fontWeight={pendingTipoEstablecimiento != null ? 600 : 400}
                  >
                    {pendingTipoEstablecimiento != null
                      ? TIPO_CAMPO_LABELS[pendingTipoEstablecimiento]
                      : detail.tipoEstablecimiento || "-"}
                  </Text>
                  {isPending && canWrite && isAltaEstablecimiento && (
                    <Pencil
                      style={{ marginLeft: "8px" }}
                      size={14}
                      cursor="pointer"
                      color={AppColors.GREEN_PRIMARY}
                      onClick={onOpenTipoCampoModal}
                    />
                  )}
                </Flex>
                {hasCoordinates && (
                  <InfoRow label="Coordenadas" value={coordenadasStr} even={false} />
                )}
                <EstadoRow
                  estado={detail.estado as EstablishmentFieldRequestStatus}
                  even={hasCoordinates}
                />
              </>
            )}
          </Flex>
        </Box>
      </Flex>
    </Flex>
  );
};
